// src/slices/roleManagement/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RoleGroup, RoleGroupDetail, Module, Permission, ModulePermissions, PageActionInputItem, RolePageActionInputAssignment, ApiPermissionItem } from 'pages/RoleManagement/types';
import {
  fetchRoleGroups,
  fetchRoleGroupDetail,
  fetchModules,
  fetchPermissions,
  fetchPermissionsByModule,
  createNewRoleGroup,
  updateExistingRoleGroup,
  deleteRoleGroupById,
  assignRolePermissionsForRole,
  fetchRolePageActionInputs,
  fetchPageActionInputsByUrlPage,
  assignRolePageActionInputsForRole,
  fetchApiPermissions,
  fetchRoleApiPermissions,
  assignRoleApiPermissionsForRole,
  fetchRoleModules,
  assignRoleModulesForRole,
} from './thunk';

interface RoleManagementState {
  roleGroups: RoleGroup[];
  modules: Module[];
  permissions: Permission[];
  permissionsByModule: ModulePermissions[];
  selectedRoleGroup: RoleGroupDetail | null;
  pageActionInputs: PageActionInputItem[];
  assignedPageActionInputs: RolePageActionInputAssignment[];
  apiPermissions: ApiPermissionItem[];
  assignedApiPermissionIds: number[];
  assignedModuleIds: number[];
  loading: boolean;
  error: string | null;
  modalState: {
    create: boolean;
    edit: boolean;
    delete: boolean;
    permissionAssign: boolean;
    apiPermissionAssign: boolean;
  };
  pagination: {
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const initialState: RoleManagementState = {
  roleGroups: [],
  modules: [],
  permissions: [],
  permissionsByModule: [],
  selectedRoleGroup: null,
  pageActionInputs: [],
  assignedPageActionInputs: [],
  apiPermissions: [],
  assignedApiPermissionIds: [],
  assignedModuleIds: [],
  loading: false,
  error: null,
  modalState: {
    create: false,
    edit: false,
    delete: false,
    permissionAssign: false,
    apiPermissionAssign: false,
  },
  pagination: {
    pageIndex: 1,
    pageSize: 100,
    totalCount: 0,
    totalPages: 0,
  },
};

const roleManagementSlice = createSlice({
  name: 'RoleManagement',
  initialState,
  reducers: {
    setRoleGroups: (state, action: PayloadAction<RoleGroup[]>) => {
      state.roleGroups = action.payload;
      state.loading = false;
    },
    setModules: (state, action: PayloadAction<Module[]>) => {
      state.modules = action.payload;
    },
    setPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
    },
    setPermissionsByModule: (state, action: PayloadAction<ModulePermissions[]>) => {
      state.permissionsByModule = action.payload;
    },
        setSelectedRoleGroup: (state, action: PayloadAction<RoleGroup | RoleGroupDetail | null>) => {
      // RoleGroup (list DTO) giờ đã luôn có sẵn moduleIds từ backend, không cần convert nữa
      state.selectedRoleGroup = action.payload as RoleGroupDetail | null;
    },
    addRoleGroup: (state, action: PayloadAction<RoleGroup>) => {
      state.roleGroups.push(action.payload);
    },
    updateRoleGroup: (state, action: PayloadAction<RoleGroup>) => {
      const index = state.roleGroups.findIndex(r => r.id === action.payload.id);
      if (index !== -1) {
        state.roleGroups[index] = action.payload;
      }
    },
    deleteRoleGroup: (state, action: PayloadAction<number>) => {
      state.roleGroups = state.roleGroups.filter(r => r.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof typeof state.modalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof typeof state.modalState>) => {
      state.modalState[action.payload] = false;
    },
    closeAllModals: (state) => {
      state.modalState = {
        create: false,
        edit: false,
        delete: false,
        permissionAssign: false,
        apiPermissionAssign: false,
      };
    },
    clearPageActionInputs: (state) => {
      state.pageActionInputs = [];
    },
    // Clear module đã gán của role trước đó — tránh flash/cache dữ liệu cũ trong lúc chờ
    // fetchRoleModules của role mới trả về (đổi role trong popup Cập Nhật Permission).
    clearAssignedModuleIds: (state) => {
      state.assignedModuleIds = [];
    },
    setPagination: (state, action: PayloadAction<Partial<RoleManagementState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch role groups
      .addCase(fetchRoleGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoleGroups.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.roleGroups = payload.items || payload.data || payload || [];
        state.pagination.totalCount = payload.totalItems ?? payload.totalCount ?? payload.total ?? state.roleGroups.length;
        state.pagination.totalPages = payload.totalPages ?? Math.ceil(state.pagination.totalCount / state.pagination.pageSize);
      })
      .addCase(fetchRoleGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
            // Fetch role group detail
            .addCase(fetchRoleGroupDetail.fulfilled, (state, action) => {
              state.selectedRoleGroup = action.payload as unknown as RoleGroupDetail;
            })
      
      // Fetch modules
      .addCase(fetchModules.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.modules = payload.items || payload.data || payload || [];
      })
      
      // Fetch permissions
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.permissions = payload.items || payload.data || payload || [];
      })
      
      // Fetch permissions by module
      .addCase(fetchPermissionsByModule.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.permissionsByModule = payload.modules || payload.items || payload.data || payload || [];
      })
      
      // Create role group
      .addCase(createNewRoleGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(createNewRoleGroup.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object' && 'id' in action.payload) {
          state.roleGroups.unshift(action.payload as unknown as RoleGroup);
        }
        state.modalState.create = false;
      })
      .addCase(createNewRoleGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update role group
      .addCase(updateExistingRoleGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateExistingRoleGroup.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object' && 'id' in action.payload) {
          const payload = action.payload as unknown as RoleGroup;
          const index = state.roleGroups.findIndex(r => r.id === payload.id);
          if (index !== -1) {
            state.roleGroups[index] = payload;
          }
        }
        state.modalState.edit = false;
        state.selectedRoleGroup = null;
      })
      .addCase(updateExistingRoleGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete role group
      .addCase(deleteRoleGroupById.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRoleGroupById.fulfilled, (state, action) => {
        state.loading = false;
        state.roleGroups = state.roleGroups.filter(r => r.id !== action.payload);
        state.modalState.delete = false;
        state.selectedRoleGroup = null;
      })
      .addCase(deleteRoleGroupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Assign role permissions (theo module)
      .addCase(assignRolePermissionsForRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignRolePermissionsForRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(assignRolePermissionsForRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch PageActionInput đã gán cho role
      .addCase(fetchRolePageActionInputs.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.assignedPageActionInputs = payload.items || payload.data || payload || [];
      })

      // Fetch PageActionInput theo UrlPage
      .addCase(fetchPageActionInputsByUrlPage.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.pageActionInputs = payload.items || payload.data || payload || [];
      })

      // Assign PageActionInput cho role
      .addCase(assignRolePageActionInputsForRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignRolePageActionInputsForRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(assignRolePageActionInputsForRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch catalog API permission (popup "Phân quyền API")
      .addCase(fetchApiPermissions.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.apiPermissions = payload.items || payload.data || payload || [];
      })

      // Fetch API permission đã gán cho role
      .addCase(fetchRoleApiPermissions.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.assignedApiPermissionIds = payload.items || payload.data || payload || [];
      })

      // Gán API permission cho role
      .addCase(assignRoleApiPermissionsForRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignRoleApiPermissionsForRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(assignRoleApiPermissionsForRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Module (menu) đã gán cho role — popup "Cập nhật permission"
      .addCase(fetchRoleModules.fulfilled, (state, action) => {
        const payload = action.payload as any;
        state.assignedModuleIds = payload.items || payload.data || payload || [];
      })

      // Gán Module (menu) cho role
      .addCase(assignRoleModulesForRole.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignRoleModulesForRole.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(assignRoleModulesForRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setRoleGroups,
  setModules,
  setPermissions,
  setPermissionsByModule,
  setSelectedRoleGroup,
  addRoleGroup,
  updateRoleGroup,
  deleteRoleGroup,
  setLoading,
  setError,
  clearError,
  openModal,
  closeModal,
  closeAllModals,
  clearPageActionInputs,
  clearAssignedModuleIds,
  setPagination,
} = roleManagementSlice.actions;

export default roleManagementSlice.reducer;
